import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Device } from '@twilio/voice-sdk';
import axios from 'axios';
import phoneIcon from '../../assets/phoneIcon.svg';
// import micOffIcon from '../../assets/micOff.png';
import { useSearchParams, useNavigate } from 'react-router-dom';


const TwilioVoice = ({ apiUrl, hostId, address, queryString, onCallEnd }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [connection, setConnection] = useState(null);
  const [callStatus, setCallStatus] = useState('initializing');
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState(null);
  const [timerInterval, setTimerInterval] = useState(null);
  const [primaryContact, setPrimaryContact] = useState(null);
  const [fallbackCount, setFallbackCount] = useState(0);
  const [location, setLocation] = useState(null);

  // Request geolocation on mount
  useEffect(() => {
    const successHandler = (position) => {
      setLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy || 100,
      });
    };

    const errorHandler = (err) => {
      console.error('Geolocation error:', err);
      setError('Location permission is required to make calls');
      setCallStatus('error');
    };

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      setCallStatus('error');
      return;
    }

    navigator.geolocation.getCurrentPosition(successHandler, errorHandler, { enableHighAccuracy: true, timeout: 10000 });
  }, []);

  // Initialize device once we have location
  useEffect(() => {
    if (!location) return;

    initializeDevice();
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const initializeDevice = async () => {
    try {
      setCallStatus('initializing');

      console.log('Requesting microphone permission...');
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const identity = `visitor_${hostId}_${Date.now()}`;

      const response = await axios.post(`${apiUrl}/call/generate-webrtc`, {
        hostId: hostId,
        queryString: queryString,
        identity: identity,
        geo: location
      }, { timeout: 15000 });

      const { token, callParams, primaryContact, fallbackCount } = response.data;

      if (!response.data.success || !token) {
        throw new Error('Failed to initialize call');
      }

      setPrimaryContact(primaryContact);
      setFallbackCount(fallbackCount);

      const twilioDevice = new Device(token, {
        codecPreferences: ['opus', 'pcmu'],
        enableRingingState: true,
        logLevel: 1
      });

      setupDeviceEvents(twilioDevice);

      await twilioDevice.register();

      setDevice(twilioDevice);

      await initiateCall(twilioDevice, callParams);

    } catch (err) {
      console.error('Failed to initialize device:', err);

      let errorMessage = 'Failed to start call. Please try again.';

      if (err && err.response) {
        // Backend error
        console.error('Backend error details:', err.response.data);
        errorMessage = err.response.data?.message || err.response.data?.error || errorMessage;
      } else if (err && err.message) {
        console.error('Error message:', err.message);
        errorMessage = err.message;
      } else {
        console.error('Unknown error type:', typeof err, err);
      }

      setError(errorMessage);
      setCallStatus('error');
    }
  };

  const setupDeviceEvents = (twilioDevice) => {
    twilioDevice.on('registered', () => {
      console.log('Device registered');
    });

    twilioDevice.on('error', (error) => {
      console.error('Device error:', error);
      setError(error.message || 'Device error occurred');
      setCallStatus('error');
    });

    twilioDevice.on('tokenWillExpire', async () => {
      try {
        const identity = `visitor_${hostId}_${Date.now()}`;
        const tokenResponse = await axios.post(`${apiUrl}/call/generate-webrtc`, {
          hostId,
          queryString,
          identity: identity,
          geo: location
        });
        twilioDevice.updateToken(tokenResponse.data.token);
      } catch (err) {
        console.error('Failed to refresh token:', err);
      }
    });
  };

  const initiateCall = async (twilioDevice, callParams) => {
    try {
      // Connect via Twilio Device using callParams from initial response

      const conn = await twilioDevice.connect({
        params: callParams
      });

      setConnection(conn);
      setupConnectionEvents(conn);

    } catch (err) {
      console.error('Failed to initiate call:', err);
      console.error('Call error details:', err.message);

      let errorMessage = 'Failed to connect call. Please try again.';
      if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setCallStatus('error');
    }
  };

  const setupConnectionEvents = (conn) => {
    conn.on('ringing', () => {
      setCallStatus('ringing');
    });

    conn.on('accept', () => {
      setCallStatus('connected');
      startCallTimer();
    });

    conn.on('disconnect', () => {
      setCallStatus('disconnected');
      stopCallTimer();
      setTimeout(() => {
        if (onCallEnd) onCallEnd();
      }, 2000);
    });

    conn.on('error', (error) => {
      console.error('Connection error:', error);
      setError(error.message);
      setCallStatus('error');
      stopCallTimer();
    });

    conn.on('cancel', () => {
      console.log('Call cancelled');
      setCallStatus('disconnected');
      stopCallTimer();
    });
  };

  const startCallTimer = () => {
    const interval = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
    setTimerInterval(interval);
  };

  const stopCallTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleMute = () => {
    if (connection) {
      const newMuteState = !isMuted;
      connection.mute(newMuteState);
      setIsMuted(newMuteState);
    }
  };

  const hangUp = () => {
    if (connection) {
      connection.disconnect();
    }
  };

  const cleanup = () => {
    stopCallTimer();
    if (connection) {
      connection.disconnect();
    }
    if (device) {
      device.unregister();
      device.destroy();
    }
  };

  const handleBack = () => {
    hangUp();
    navigate(`/directory?${searchParams.toString()}`);
  };

  const getStatusText = () => {
    switch (callStatus) {
      case 'initializing': return 'Connecting to voice service...';
      case 'ringing': return 'Ringing...';
      case 'connecting': return 'Connecting call...';
      case 'connected': return `Connected ${formatDuration(callDuration)}`;
      case 'disconnected': return 'Call ended';
      case 'error': return error || 'Call failed';
      default: return '';
    }
  };

  return (
    <VoiceContainer>
      <TopBarDiv>
        <BackButton onClick={handleBack} style={{position: "absolute", top: "20px", left: "20px", color: "white"}}>⬅</BackButton>
      </TopBarDiv>
      <StatusSection>
        <ContactAvatar>
          {/* <AvatarIcon>📞</AvatarIcon> */}
          <ButtonLabel><img id="phoneicon" alt="phone icon" src={phoneIcon} style={{width: "4em"}}></img></ButtonLabel>
        </ContactAvatar>
        <ContactName>{address || 'Contact'}</ContactName>
        <CallStatus status={callStatus}>
          {getStatusText()}
        </CallStatus>
      </StatusSection>

      <ControlsSection>
        <ControlButton
          onClick={toggleMute}
          active={isMuted}
          disabled={callStatus !== 'connected'}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {/* <ButtonIcon>{isMuted ? '🔇' : '🎤'}</ButtonIcon> */}
          <ButtonLabel>{isMuted ? 'Muted' : 'Mute'}</ButtonLabel>
        </ControlButton>

        <HangUpButton
          onClick={hangUp}
          disabled={callStatus === 'disconnected' || callStatus === 'error'}
          aria-label="Hang up"
        >
          {/* <ButtonIcon>📞</ButtonIcon> */}
          <ButtonLabel>End Call</ButtonLabel>
        </HangUpButton>
      </ControlsSection>

      {error && callStatus === 'error' && (
        <ErrorSection>
          <ErrorText>{error}</ErrorText>
        </ErrorSection>
      )}
    </VoiceContainer>
  );
};

export default TwilioVoice;

const VoiceContainer = styled.div`
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px 20px;
  box-sizing: border-box;
`;

const StatusSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ContactAvatar = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: #222;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
`;

const AvatarIcon = styled.div`
  font-size: 60px;
`;

const ContactName = styled.h2`
  color: #fff;
  font-size: 24px;
  margin: 16px 0;
  text-align: center;
`;

const ContactInfo = styled.div`
  color: #ccc;
  font-size: 14px;
  margin-top: 8px;
  text-align: center;
`;

const CallStatus = styled.div`
  color: ${props => {
    if (props.status === 'connected') return '#4caf50';
    if (props.status === 'error') return '#f44336';
    return '#fff';
  }};
  font-size: 18px;
  margin-top: 8px;
  text-align: center;
`;

const ControlsSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: 20px 0;
`;

const ControlButton = styled.button`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.active ? '#B70100' : '#666'};
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.active ? '#9e0100' : '#555'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HangUpButton = styled(ControlButton)`
  background: #f44336;

  &:hover:not(:disabled) {
    background: #d32f2f;
  }
`;

const ButtonIcon = styled.div`
  font-size: 28px;
  margin-bottom: 4px;
`;

const ButtonLabel = styled.div`
  font-size: 12px;
`;

const ErrorSection = styled.div`
  padding: 16px;
  background: rgba(255, 67, 54, 0.1);
  border-radius: 8px;
  margin-top: 20px;
`;

const ErrorText = styled.div`
  color: #ffb3b3;
  text-align: center;
  font-size: 14px;
`;

const TopBarDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  position: relative;
  margin-bottom: 30px;

  h1 {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    margin: 0;
  }
`;

const BackButton = styled.button`
  padding: 10px 20px 10px 5px;
  font-size: 16px;
  background-color: black;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #B70100;
  }

  &:active {
    background-color: #821010ff;
  }
`;
